import { copyToClipboard } from '@xver/utils/clipboard';
import { IconCheck, IconCopy } from '@tabler/icons-react';
import type { TextareaHTMLAttributes } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../Button/Button';

type EditorSize = 'sm' | 'md' | 'lg';
type EditorTextSize = '2xs' | 'xs' | 'sm' | 'md' | 'lg';

interface EditorProps {
    defaultValue?: string;
    initialValue?: string;
    filename?: string;
    placeholder?: string;
    readOnly?: boolean;
    height?: number | string;
    enableCopy?: boolean;
    hideScrollbar?: boolean;
    disableWrap?: boolean;
    hideScrollbarX?: boolean;
    hideScrollbarY?: boolean;
    hideLineCharsLabel?: boolean;
    size?: EditorSize;
    textSize?: EditorTextSize;
    onChange?: (value: string) => void;
    /** Spread onto the <textarea>; use for aria-label / aria-invalid / aria-describedby. */
    textareaProps?: TextareaHTMLAttributes<HTMLTextAreaElement>;
}

export default function Editor({
    defaultValue = '',
    initialValue = '',
    filename = 'startup.sh',
    placeholder = '',
    readOnly = false,
    height = '12rem',
    enableCopy = false,
    hideScrollbar = false,
    disableWrap = false,
    hideScrollbarX = false,
    hideScrollbarY = false,
    hideLineCharsLabel = false,
    size = 'md',
    textSize,
    onChange,
    textareaProps,
}: EditorProps) {
    const { t } = useTranslation('common');
    const initial = defaultValue || initialValue || '';
    const [value, setValue] = useState(initial);
    const [copied, setCopied] = useState(false);
    const copiedTimerRef = useRef<number | null>(null);
    const generatedId = useId();
    const textareaId = textareaProps?.id ?? generatedId;

    const lines = useMemo(() => value.split('\n'), [value]);
    const chars = value.length;

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(e.target.value);
        onChange?.(e.target.value);
    };

    const heightValue = typeof height === 'number' ? `${height}px` : height;

    const headerPaddingClass =
        size === 'sm' ? 'px-3 py-1.5' : size === 'lg' ? 'px-5 py-3' : 'px-4 py-2';
    const filenameTextClass = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm';
    const metaTextClass = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm';
    const textareaTextClass = size === 'sm' ? 'text-xs' : size === 'lg' ? 'text-base' : 'text-sm';
    const textareaPaddingClass = size === 'sm' ? 'p-2.5' : size === 'lg' ? 'p-4' : 'p-3';
    const textareaContentTextClass =
        textSize === '2xs'
            ? 'text-[9px]'
            : textSize === 'xs'
              ? 'text-[10px]'
              : textSize === 'sm'
                ? 'text-xs'
                : textSize === 'lg'
                  ? 'text-base'
                  : textSize === 'md'
                    ? 'text-sm'
                    : textareaTextClass;
    const textareaFontSize =
        textSize === '2xs'
            ? '9px'
            : textSize === 'xs'
              ? '10px'
              : textSize === 'sm'
                ? '12px'
                : textSize === 'md'
                  ? '14px'
                  : textSize === 'lg'
                    ? '16px'
                    : undefined;

    const getScrollControlCss = () => {
        if (hideScrollbarX && hideScrollbarY) {
            return `.editor-scroll-control::-webkit-scrollbar{display:none}.editor-scroll-control{scrollbar-width:none;-ms-overflow-style:none}`;
        }
        return '';
    };

    const getWrapperOverflowClass = () => {
        if (hideScrollbarX && hideScrollbarY) return 'overflow-hidden';
        if (hideScrollbarX && !hideScrollbarY) return 'overflow-x-hidden overflow-y-auto';
        if (hideScrollbarY && !hideScrollbarX) return 'overflow-x-auto overflow-y-hidden';
        return 'overflow-hidden';
    };

    useEffect(() => {
        return () => {
            if (copiedTimerRef.current) {
                window.clearTimeout(copiedTimerRef.current);
            }
        };
    }, []);

    useEffect(() => {
        const latest = defaultValue || initialValue || '';
        if (readOnly) {
            setValue(latest);
        }
    }, [defaultValue, initialValue, readOnly]);

    const handleCopy = async () => {
        const textToCopy = value || defaultValue || initialValue || placeholder || '';

        try {
            await copyToClipboard(textToCopy);

            if (copiedTimerRef.current) {
                window.clearTimeout(copiedTimerRef.current);
            }

            setCopied(true);
            copiedTimerRef.current = window.setTimeout(() => {
                setCopied(false);
            }, 1500);
        } catch {
            return;
        }
    };

    return (
        <div className="w-full">
            {hideScrollbar && (
                <style>{`
.editor-hide-scrollbar::-webkit-scrollbar{display:none}
.editor-hide-scrollbar{scrollbar-width:none;-ms-overflow-style:none}
`}</style>
            )}
            {(hideScrollbarX || hideScrollbarY) && <style>{getScrollControlCss()}</style>}
            <div
                className={`flex items-center justify-between bg-surface-2 rounded-t-md border border-b-0 border-line ${headerPaddingClass}`}
            >
                <div className={`flex items-center gap-3 text-fg/80 ${filenameTextClass}`}>
                    <span className="w-2 h-2 rounded-full bg-line-strong inline-block" />
                    <label htmlFor={textareaId} className="text-sm font-mono">
                        {filename}
                    </label>
                </div>
                <div className="flex items-center gap-2">
                    {!hideLineCharsLabel && (
                        <div className={`${enableCopy ? 'text-[15px]' : metaTextClass} text-fg/68`}>
                            {t('editor.lines', { count: lines.length })} ·{' '}
                            {t('editor.chars', { count: chars })}
                        </div>
                    )}
                    {enableCopy && (
                        <Button
                            type="button"
                            size="sm"
                            onClick={handleCopy}
                            leftIcon={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                            bgColor={copied ? 'rgb(22, 163, 74)' : '#1b1b1d'}
                            borderColor={copied ? 'rgb(22, 163, 74)' : '#333333'}
                            borderWidth={1}
                            textColor="#fff"
                            padding="0.25rem 0.55rem"
                            fontSize={12}
                        >
                            {copied ? t('copied') : t('copy')}
                        </Button>
                    )}
                </div>
            </div>

            <div
                className={`border border-line rounded-b-md relative bg-page focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-(--color-accent) ${hideScrollbarX || hideScrollbarY ? getWrapperOverflowClass() : 'overflow-hidden'}`}
            >
                <textarea
                    id={textareaId}
                    value={value}
                    placeholder={placeholder}
                    onChange={handleChange}
                    readOnly={readOnly}
                    wrap={disableWrap ? 'off' : 'soft'}
                    style={{
                        height: heightValue,
                        fontSize: textareaFontSize,
                        display: 'block',
                        overflowX: hideScrollbarX
                            ? 'hidden'
                            : hideScrollbarY && disableWrap
                              ? 'scroll'
                              : 'auto',
                        overflowY: hideScrollbarY ? 'hidden' : 'auto',
                        whiteSpace: disableWrap ? 'pre' : undefined,
                        overflowWrap: disableWrap ? 'normal' : undefined,
                        wordBreak: disableWrap ? 'normal' : undefined,
                    }}
                    className={`w-full bg-page text-fg placeholder:text-fg/68 font-mono resize-none border-0 shadow-none appearance-none outline-none focus:outline-none focus:ring-0 focus:border-0 ${textareaPaddingClass} ${textareaTextClass} ${textareaContentTextClass} ${
                        readOnly ? 'opacity-90' : ''
                    } ${hideScrollbar ? 'editor-hide-scrollbar' : ''} ${hideScrollbarX || hideScrollbarY ? 'editor-scroll-control' : ''}`}
                    spellCheck={false}
                    {...textareaProps}
                />
            </div>
        </div>
    );
}
